import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { useEffect, useRef } from 'react';
import styled from '../../styles/styled';
import { ResizeObserver as Polyfill } from '@juggle/resize-observer';
class AutoGridLogic {
    /**
     * connect will start monitoring a div element (which should have display: grid) for resize events.
     * On resize events, the css grid items will automatically be resized based on the config's aspect ratio
     * @param parentElement The HTMLDivElement that is a CSS Grid
     */
    connect(parentElement, autoGridConfig) {
        if (!window.ResizeObserver) {
            window.ResizeObserver = Polyfill;
        }
        // listen for the container resizing
        const resizeObserver = new ResizeObserver(() => {
            if (parentElement && autoGridConfig) {
                this.organizeContainerItems(parentElement, autoGridConfig);
            }
        });
        // listen for children being added or removed
        const childObserver = new MutationObserver(mutations => {
            for (let i = 0; i < mutations.length; i++) {
                if (mutations[i].type === 'childList') {
                    this.organizeContainerItems(parentElement, autoGridConfig);
                }
            }
        });
        childObserver.observe(parentElement, { childList: true });
        resizeObserver.observe(parentElement);
        // Immediately resize items
        requestAnimationFrame(() => {
            const parentComputedStyles = window.getComputedStyle(parentElement);
            if (parentComputedStyles.display !== 'grid') {
                resizeObserver.unobserve(parentElement);
                childObserver.disconnect();
                return;
            }
            this.organizeContainerItems(parentElement, autoGridConfig);
        });
        return () => {
            childObserver.disconnect();
            resizeObserver.unobserve(parentElement);
            if (this.animationFrameId) {
                cancelAnimationFrame(this.animationFrameId);
            }
        };
    }
    organizeContainerItems(parent, requirements) {
        // If we do not have children, then we cannot resize them.
        const children = parent.children;
        if (!children.length) {
            return;
        }
        // Obtain the dimensions of the parent element
        const parentDOMRect = parent.getBoundingClientRect();
        // Get best item dimensions and apply property
        const dimensions = this.getGridItemDimensions({
            parentWidth: parentDOMRect.width,
            parentHeight: parentDOMRect.height,
            childrenCount: children.length,
            requirements,
        });
        parent.style.setProperty('--auto-grid-height', parentDOMRect.height + 'px');
        parent.style.setProperty('--item-width', dimensions.width + 'px');
        // If the best is more than one row but the items actually can fit in one row it will default to one row unless we set this to null
        parent.style.setProperty('--item-row-width', dimensions.rows <= 1 ? dimensions.width + 'px' : null);
        parent.style.setProperty('--item-height', dimensions.height + 'px');
        parent.style.setProperty('--item-row-height', dimensions.rows > 1 ? dimensions.height + 'px' : null);
    }
    getGridItemDimensions(config) {
        const parentAspectRatio = config.parentWidth / config.parentHeight;
        // Arbitrary width and height of a grid item based on our aspect ratio
        // This is a refrence for us to calculate the best fit for all the items
        const mockCssGridItem = {
            height: 100,
            width: 100 * config.requirements.aspectRatio,
        };
        // Our best fit is determined by finding the aspect ratio of the grid that matches the parent's aspect ratio
        const bestFit = {
            rows: 0,
            columns: 0,
            gridRatio: 0,
        };
        let rowCount = 1;
        while (rowCount <= config.childrenCount) {
            // Find the number of columns we should have based on the number of children and the row we are one
            // rows * columns = numberOfChildren
            // columns = numberOfChildren / rows
            const columnCount = Math.ceil(config.childrenCount / rowCount);
            // Find the aspect ratio of grid using the calculated columns, potential rows, and our mock grid item.
            const candidateAspectRatio = (mockCssGridItem.width * columnCount) / (mockCssGridItem.height * rowCount);
            // Calculate the difference between the candidateAspectRatio and the parentAspectRatio
            // Calculate the difference between the current bestFit.gridRatio and the parentAspectRatio
            // The smallest difference is desirable because then the grid aspect ratio is similar to the parent aspect ratio
            const candidateAspectRatioIsSmallerThanBestFitRatio = () => Math.abs(candidateAspectRatio - parentAspectRatio) <
                Math.abs(bestFit.gridRatio - parentAspectRatio);
            if (!bestFit.gridRatio || candidateAspectRatioIsSmallerThanBestFitRatio()) {
                bestFit.rows = rowCount;
                bestFit.columns = columnCount;
                bestFit.gridRatio = candidateAspectRatio;
            }
            rowCount++;
        }
        // Build the grid item's width and height.
        // If the bestFit.gridRatio is less then the parentAspectRatio,
        // then the item's height is determined by the amount of rows.
        //
        // If the bestFit.gradRatio is greater than the parentAspectRation, then
        // item's width is determined by the amount of columns.
        const result = {
            height: 0,
            width: 0,
            rows: 0,
        };
        // Scale the child to the parent's width and height
        // and override the required aspect ratio
        if (config.childrenCount === 1 &&
            config.requirements?.options?.fillScreenWhenOneChild === true) {
            result.height = config.parentHeight;
            result.width = config.parentWidth;
        }
        else {
            if (bestFit.gridRatio < parentAspectRatio) {
                // parent is wider than the grid
                result.height =
                    (config.parentHeight - config.requirements.minimumGap * bestFit.rows) /
                        bestFit.rows;
                result.width = result.height * config.requirements.aspectRatio;
            }
            else {
                // parent is taller than the grid
                result.width =
                    (config.parentWidth - config.requirements.minimumGap * bestFit.columns) /
                        bestFit.columns;
                result.height = result.width / config.requirements.aspectRatio;
            }
        }
        result.rows = bestFit.rows;
        // If the determined width is very close in size to the parent width, we need to move the arrow that emphasizes the
        // camera flip button to the left instead of the right
        if (config.requirements?.options?.setSmallGap) {
            if (config.parentWidth - result.width <= 10) {
                config.requirements.options.setSmallGap(true);
            }
            else
                config.requirements.options.setSmallGap(false);
        }
        return result;
    }
}
const AutoGridContainer = styled.div(() => ({
    position: 'absolute',
    height: '100%',
    width: '100%',
    display: 'grid',
    gridTemplateRows: 'repeat(auto-fit, var(--item-row-height))',
    gridTemplateColumns: 'repeat(auto-fit, var(--item-row-width))',
    placeContent: 'space-evenly',
}));
export function AutoGrid({ aspectRatio, minimumGap, children, options }) {
    const container = useRef(null);
    useEffect(() => {
        if (!container.current)
            return;
        const autoGrid = new AutoGridLogic();
        const stopAutoGrid = autoGrid.connect(container.current, {
            aspectRatio: aspectRatio,
            minimumGap: minimumGap,
            options: options,
        });
        return () => stopAutoGrid();
    }, [container, aspectRatio, minimumGap, options]);
    return _jsx(AutoGridContainer, { ref: container, children: children });
}
//# sourceMappingURL=auto_grid.js.map